import client from './inventory-request';
import { withStoreCreateBody } from '../hooks/useStoreScope';

function ss(key) {
  const v = sessionStorage.getItem(key);
  return v && v !== 'null' && v !== 'undefined' ? v : null;
}

function poScopeParams(scope = {}) {
  const params = {};
  if (scope.tenant_id) params.tenant_id = scope.tenant_id;
  if (scope.hospital_id) params.hospital_id = scope.hospital_id;
  return params;
}

function userId() {
  return ss('USER_ACCESS_KEY');
}

function poWorkflowBody(opts = {}, actorField) {
  const body = {};
  const actor = opts[actorField] || userId();
  if (actor) body[actorField] = actor;
  if (opts.remarks !== undefined) body.remarks = opts.remarks;
  return body;
}

// API: POST /inventory/purchase-orders/{po_id}/{action}/ — dedicated sub-resource action
function postPoWorkflow(action, poId, body, scope = {}) {
  if (!poId) {
    throw new Error('Purchase order id is required for workflow actions');
  }
  return client.post(
    `/inventory/purchase-orders/${poId}/${action}/`,
    body,
    poScopeParams(scope),
  );
}

// Purchase Orders
// API: GET /inventory/purchase-orders/
export const getPurchaseOrders    = (p) => client.get('/inventory/purchase-orders/', p);
// API: GET /inventory/purchase-orders/{id}/
export const getPurchaseOrder     = (id) => client.get(`/inventory/purchase-orders/${id}/`);
// API: POST /inventory/purchase-orders/
export const createPurchaseOrder  = (body) => client.post('/inventory/purchase-orders/', body);
// API: PATCH /inventory/purchase-orders/{id}/
export const updatePurchaseOrder  = (id, body) => client.patch(`/inventory/purchase-orders/${id}/`, body);
// API: DELETE /inventory/purchase-orders/{id}/ — soft-delete; only DRAFT or CANCELLED POs
export const deletePurchaseOrder  = (id, scope = {}) =>
  client.delete(`/inventory/purchase-orders/${id}/`, poScopeParams(scope));

/** Backend allows soft-delete only for unfinalized POs (DRAFT or CANCELLED, still active). */
export function canDeletePo(po) {
  if (!po || po.is_active === false) return false;
  return ['DRAFT', 'CANCELLED'].includes(po.status);
}

// API: POST /inventory/purchase-orders/{id}/submit-for-approval/
// Precondition: PO status must be DRAFT or AMENDED.
export const submitPoForApproval  = (id, opts = {}) =>
  postPoWorkflow('submit-for-approval', id, poWorkflowBody(opts, 'submitted_by'), opts);

// API: POST /inventory/purchase-orders/{id}/approve/
// Precondition: PO status must be PENDING_APPROVAL. approved_by is required.
export const approvePo            = (id, opts = {}) =>
  postPoWorkflow('approve', id, poWorkflowBody(opts, 'approved_by'), opts);

// API: POST /inventory/purchase-orders/{id}/reject/
// rejection_reason is required. revert_to_draft (default true) sends PO back to
// DRAFT instead of CANCELLED.
export const rejectPo             = (id, opts = {}) => {
  const body = poWorkflowBody(opts, 'rejected_by');
  body.rejection_reason = opts.reason || opts.rejection_reason || '';
  if (opts.revert_to_draft !== undefined) body.revert_to_draft = opts.revert_to_draft;
  return postPoWorkflow('reject', id, body, opts);
};

// API: POST /inventory/purchase-orders/{id}/send-to-vendor/
// Precondition: PO status must be APPROVED.
export const sendPoToVendor       = (id, opts = {}) =>
  postPoWorkflow('send-to-vendor', id, poWorkflowBody(opts, 'sent_by'), opts);

// API: POST /inventory/purchase-orders/{id}/cancel/
// cancellation_reason is required.
export const cancelPo             = (id, opts = {}) => {
  const body = poWorkflowBody(opts, 'cancelled_by');
  body.cancellation_reason = opts.reason || opts.cancellation_reason || '';
  return postPoWorkflow('cancel', id, body, opts);
};

// API: POST /inventory/purchase-orders/{id}/close/
// force_close=true bypasses the FULLY_RECEIVED/PARTIALLY_RECEIVED precondition
// — this is the "short close" path for POs with items still pending.
export const closePo              = (id, opts = {}) => {
  const body = poWorkflowBody(opts, 'closed_by');
  if (opts.reason) body.reason = opts.reason;
  if (opts.force_close !== undefined) body.force_close = opts.force_close;
  return postPoWorkflow('close', id, body, opts);
};

// PO Items
// API: GET /inventory/purchase-order-items/ — filter by po_id
export const getPurchaseOrderItems    = (poId, scope = {}) => {
  const params = { po_id: poId, purchase_order_id: poId };
  if (scope.tenant_id) params.tenant_id = scope.tenant_id;
  if (scope.hospital_id) params.hospital_id = scope.hospital_id;
  return client.get('/inventory/purchase-order-items/', params);
};
// API: GET /inventory/purchase-order-items/{id}/
export const getPurchaseOrderItem     = (id) => client.get(`/inventory/purchase-order-items/${id}/`);
// API: POST /inventory/purchase-order-items/
export const createPurchaseOrderItem  = (body) => client.post('/inventory/purchase-order-items/', body);
// API: PATCH /inventory/purchase-order-items/{id}/
export const updatePurchaseOrderItem  = (id, body) => client.patch(`/inventory/purchase-order-items/${id}/`, body);
// API: POST /inventory/purchase-order-items/
export const addPurchaseOrderItem     = (poId, body) => client.post('/inventory/purchase-order-items/', { po_id: poId, ...body });

// PO from accepted quote — procurement-service
// API: POST /inventory/purchase-orders/from-accepted-quote/
export const createPoFromAcceptedQuote = (body) =>
  client.post('/inventory/purchase-orders/from-accepted-quote/', withStoreCreateBody(body));

// Approvals
// API: GET /inventory/procurement/approvals/
export const getProcurementApprovals = (p) => client.get('/inventory/procurement/approvals/', p);
